import { yupResolver } from '@hookform/resolvers/yup';
import { debounce } from 'lodash';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

import LoadingButton from '@mui/lab/LoadingButton';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Drawer,
  Grid,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkIcon from '@mui/icons-material/Link';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { useGetCompanyByIdQuery, useGetMyCompaniesQuery } from 'src/services/connections/connections.service';
import { useGetContactByIdQuery, useGetOrganizationContactsQuery } from 'src/services/contacts/contacts.service';
import { useGetAllDealsQuery, useGetDealQuery } from 'src/services/deal-flow/deal-flow.service';
import { AccountRole } from 'src/services/organisation/organisation.enum';
import { useGetAccountsQuery } from 'src/services/organisation/organisation.service';
import {
  useCreateTaskMutation,
  useGetTaskQuery,
  useUpdateTaskMutation
} from 'src/services/tasks/tasks.service';
import {
  CreateTaskDto,
  TaskPriority,
  TaskType,
  UpdateTaskDto
} from 'src/services/tasks/tasks.types';
import { useAppSelector } from 'src/store/store';

import FeatureLockedListView from 'src/components/billing/feature-locked-list-view';
import FormProvider, {
  RHFAutocomplete,
  RHFSelect,
  RHFTextField
} from 'src/components/hook-form';

import { useFeatures } from 'src/sections/billing/usage/hooks/use-features';

import { useGetApplicationByIdQuery, useGetApplicationsQuery } from 'src/services/applications/applications.service';
import { useGetJobByIdQuery, useGetMyActiveJobsQuery } from 'src/services/jobs/jobs.service';
import { FeatureType } from 'src/types/subscription.types';

// ----------------------------------------------------------------------

interface Props {
  open: boolean;
  onClose?: () => void;
  taskId?: string | null;
}

interface CompanyOption {
  id: string;
  company_name: string;
}

interface ContactOption {
  id: string;
  name: string;
  email: string;
}

interface DealOption {
  id: string;
  title: string;
}

interface JobOption {
  id: string;
  title: string;
}

interface ApplicationOption {
  id: string;
  title: string;
}

interface AccountOption {
  id: string;
  name: string;
  email: string;
}

export default function TaskEditorSideSheet({
  open,
  onClose = () => { },
  taskId = null
}: Props) {

  const authContext = useAuthContext();

  const { enqueueSnackbar } = useSnackbar();

  const { canAccess } = useFeatures();

  const { t } = useTranslate();

  const {
    dealId: injectedDealId,
    companyId: injectedCompanyId,
    contactId: injectedContactId,
    jobId: injectedJobId,
    applicationId: injectedApplicationId
  } = useAppSelector(
    (state) => state.app.taskSidesheet
  );

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [companySearchQuery, setCompanySearchQuery] = useState<string>("");
  const [contactsSearchQuery, setContactSearchQuery] = useState<string>("");
  const [accountsSearchQuery, setAccountsSearchQuery] = useState<string>("");
  const [dealsSearchQuery, setDealsSearchQuery] = useState<string>("");
  const [jobsSearchQuery, setJobsSearchQuery] = useState<string>("");
  const [applicationsSearchQuery, setApplicationsSearchQuery] = useState<string>("");
  
  // State for entity links accordion
  const [entityLinksExpanded, setEntityLinksExpanded] = useState<boolean>(false);

  const { currentData: companies, isLoading: isCompaniesLoading } = useGetMyCompaniesQuery(
    {
      params: {
        page: 1,
        per_page: 10,
        search_query: companySearchQuery
      },
    }
  );

  const { currentData: contacts, isLoading: isContactsLoading } = useGetOrganizationContactsQuery({
    org_id: authContext.organization.id,
    page: {
      page: 1,
      per_page: 10,
    },
    search_query: contactsSearchQuery
  }, {
    skip: !authContext.organization.id || !open,
  })

  const { currentData: deals, isLoading: isDealsLoading } = useGetAllDealsQuery({
    page: 1,
    perPage: 10,
    searchQuery: dealsSearchQuery
  }, {
    skip: !authContext.organization.id || !open,
  })

  const { currentData: accounts, isLoading: isAccountsLoading } = useGetAccountsQuery({
    org_id: authContext.organization.id,
    page: {
      page: 1,
      per_page: 10,
    },
    search_query: accountsSearchQuery
  }, {
    skip: !authContext.organization.id || !open,
  });

  const { currentData: jobs, isLoading: isJobsLoading } = useGetMyActiveJobsQuery({
    page: 1,
    per_page: 10,
    search_query: jobsSearchQuery
  }, {
    skip: !open,
  });

  const { currentData: jobApplications, isLoading: isApplicationsLoading } = useGetApplicationsQuery({
    params: {
      page: 1,
      per_page: 10,
      search_query: applicationsSearchQuery
    }
  }, {
    skip: !open,
  });


  // Fetch related entity details if available
  const { currentData: injectedContact } = useGetContactByIdQuery(injectedContactId as string, {
    skip: !injectedContactId || !open,
  });

  const { currentData: injectedCompany } = useGetCompanyByIdQuery({
    profile_id: injectedCompanyId as string,
  }, {
    skip: !injectedCompanyId || !open,
  });

  const { currentData: injectedDeal } = useGetDealQuery({
    dealId: injectedDealId as string
  }, {
    skip: !injectedDealId || !open,
  });

  const { currentData: injectedJob } = useGetJobByIdQuery(injectedJobId as string, {
    skip: !injectedJobId || !open,
  });

  const { currentData: injectedApplication } = useGetApplicationByIdQuery(injectedApplicationId as string, {
    skip: !injectedApplicationId || !open,
  });

  // -----------------------------------------------------

  const { currentData: task } = useGetTaskQuery({
    taskId: taskId as string
  }, {
    skip: !taskId
  });

  const [createTask, { isLoading: createTaskLoading }] = useCreateTaskMutation();
  const [updateTask, { isLoading: updateTaskLoading }] = useUpdateTaskMutation();

  const TaskFormSchema = Yup.object().shape({
    title: Yup.string().required(t('validation.required')),
    description: Yup.string().nullable(),
    priority: Yup.string().required(t('validation.required')),
    type: Yup.string().required(t('validation.required')),
    due_at: Yup.date().nullable(),
    assigned_to: Yup.mixed<AccountOption>().nullable(),
    company: Yup.mixed<CompanyOption>().nullable(),
    contact: Yup.mixed<ContactOption>().nullable(),
    deal: Yup.mixed<DealOption>().nullable(),
    job: Yup.mixed<JobOption>().nullable(),
    application: Yup.mixed<ApplicationOption>().nullable(),
  });

  const defaultValues = useMemo(() => ({
    title: '',
    description: '',
    priority: TaskPriority.NONE,
    type: TaskType.GENERAL,
    due_at: null,
    assigned_to: null as AccountOption | null,
    company: null,
    deal: null,
    contact: null,
    job: null,
    application: null,
  }), []);

  const methods = useForm({
    resolver: yupResolver(TaskFormSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    watch,
    formState: { isSubmitting: formSubmitting },
  } = methods;

  useEffect(() => {
    if (task) {
      reset({
        title: task.title,
        description: task.description,
        priority: task.priority,
        type: task.type,
        due_at: task.due_at ? moment(task.due_at).format('YYYY-MM-DDTHH:mm') : null as Date | any,
        assigned_to: task.assigned_to
          ? { id: task.assigned_to.id, name: `${task.assigned_to.first_name} ${task.assigned_to.last_name}`, email: task.assigned_to.email }
          : null,
        company: task.company_profile
          ? {
            id: task.company_profile.id,
            company_name: task.company_profile.company_name
          }
          : null,
        contact: task.contact
          ? { id: task.contact.id, name: `${task.contact.first_name} ${task.contact.last_name}`, email: task.contact.email }
          : null,
        deal: task.deal
          ? { id: task.deal.id, title: task.deal.title }
          : null,
        job: task.job
          ? {
            id: task.job.id,
            title: task.job.title
          }
          : null,
        application: task.job_application
          ? {
            id: task.job_application.id,
            title: `${task.job_application.job.title} - ${task.job_application.attached_profile.first_name} ${task.job_application.attached_profile.last_name}`
          }
          : null,
      });
    }
  }, [task, reset]);

  useEffect(() => {
    if (injectedContact) {
      reset((prev) => ({
        ...prev,
        contact: {
          id: injectedContact.id,
          name: `${injectedContact.first_name} ${injectedContact.last_name}`,
          email: injectedContact.email,
        }
      }));
    }

    if (injectedCompany) {
      reset((prev) => ({
        ...prev,
        company: {
          id: injectedCompany.id,
          company_name: injectedCompany.company_name,
        }
      }));

    }

    if (injectedDeal) {
      reset((prev) => ({
        ...prev,
        deal: {
          id: injectedDeal.id,
          title: injectedDeal.title,
        }
      }));
    }

    if (injectedJob) {
      reset((prev) => ({
        ...prev,
        job: {
          id: injectedJob.id,
          title: injectedJob.title,
        }
      }));
    }

    if (injectedApplication) {
      reset((prev) => ({
        ...prev,
        application: {
          id: injectedApplication.id,
          title: `${injectedApplication.job.title} - ${injectedApplication.attached_profile.first_name} ${injectedApplication.attached_profile.last_name}`
        }
      }));
    }

  }, [injectedContact, injectedCompany, injectedDeal, reset, injectedJob, injectedApplication]);

  const onSubmit = handleSubmit(async (data) => {
    if (formSubmitting) return;

    setErrorMessage(null);

    try {
      if (taskId) {
        // Update existing task
        const updateData: UpdateTaskDto = {
          title: data.title,
          description: data.description || '',
          priority: data.priority as TaskPriority,
          type: data.type as TaskType,
          due_at: data.due_at || undefined,
          assigned_to: data.assigned_to ? data.assigned_to.id : undefined,
          company_profile_id: data.company ? data.company.id : null,
          contact_id: data.contact ? data.contact.id : null,
          deal_id: data.deal ? data.deal.id : null,
          job_id: data.job ? data.job.id : null,
          job_application_id: data.application ? data.application.id : null,
        };

        await updateTask({
          taskId,
          data: updateData
        }).unwrap();

        enqueueSnackbar(t('tasks.api.update.success'), { variant: 'success' });
      } else {
        // Create new task
        const createData: CreateTaskDto = {
          title: data.title,
          description: data.description || '',
          priority: data.priority as TaskPriority,
          type: data.type as TaskType,
          due_at: data.due_at || undefined,
          assigned_to: data.assigned_to ? data.assigned_to.id : undefined,
          company_profile_id: data.company ? data.company.id : null,
          contact_id: data.contact ? data.contact.id : null,
          deal_id: data.deal ? data.deal.id : null,
          job_id: data.job ? data.job.id : null,
          job_application_id: data.application ? data.application.id : null,
        };

        await createTask(createData).unwrap();

        enqueueSnackbar(t('tasks.api.create.success'), { variant: 'success' });
      }

      handleClose();
    } catch (error) {
      setErrorMessage(t('tasks.api.default_error'));
    }
  });

  const handleClose = (e?: any, reason?: string) => {
    if (reason && reason === 'backdropClick') return;

    setErrorMessage(null);
    reset(defaultValues);
    onClose();
  };

  const onCompanySearchQueryChange = useMemo(
    () => debounce((value: string) => {
      setCompanySearchQuery(value);
    }, 500),
    [setCompanySearchQuery]
  );

  const onContactSearchQueryChange = useMemo(
    () => debounce((value: string) => {
      setContactSearchQuery(value);
    }, 500),
    [setContactSearchQuery]
  );

  const onAccountSearchQueryChange = useMemo(
    () => debounce((value: string) => {
      setAccountsSearchQuery(value);
    }, 500),
    [setAccountsSearchQuery]
  );

  const onDealsSearchQueryChange = useMemo(
    () => debounce((value: string) => {
      setDealsSearchQuery(value);
    }, 500),
    [setDealsSearchQuery]
  );

  const onJobsSearchQueryChange = useMemo(
    () => debounce((value: string) => {
      setJobsSearchQuery(value);
    }, 500),
    [setJobsSearchQuery]
  );

  const onApplicationsSearchQueryChange = useMemo(
    () => debounce((value: string) => {
      setApplicationsSearchQuery(value);
    }, 500),
    [setApplicationsSearchQuery]
  );

  const linkableCompanies = useMemo(() => {

    if (!companies || isCompaniesLoading) return [];

    return companies.results.map((company) => ({
      id: company.id,
      company_name: company.company_name,
    })) as CompanyOption[];

  }, [companies, isCompaniesLoading]);

  const linkableContacts = useMemo(() => {

    if (!contacts || isContactsLoading) return [];

    return contacts.results.map((contact) => ({
      id: contact.id,
      name: `${contact.first_name} ${contact.last_name}`,
      email: contact.email,
    })) as ContactOption[];

  }, [contacts, isContactsLoading]);

  const linkableDeals = useMemo(() => {

    if (!deals || isDealsLoading) return [];

    return deals.results.map((deal) => ({
      id: deal.id,
      title: deal.title
    })) as DealOption[];

  }, [deals, isDealsLoading]);

  const linkableAccounts = useMemo(() => {

    if (!accounts || isAccountsLoading) return [];

    return accounts.results.map((account) => ({
      id: account.id,
      name: `${account.first_name} ${account.last_name}`,
      email: account.email,
    })) as AccountOption[];

  }, [accounts, isAccountsLoading]);

  const linkableJobs = useMemo(() => {
    if (!jobs || isJobsLoading) return [];

    return jobs.results.map((job) => ({
      id: job.id,
      title: job.title
    })) as JobOption[];
  }, [jobs, isJobsLoading]);

  const linkableApplications = useMemo(() => {
    if (!jobApplications || isApplicationsLoading) return [];

    return jobApplications.results.map((application) => ({
      id: application.id,
      title: `${application.job.title} - ${application.attached_profile.first_name} ${application.attached_profile.last_name}`
    })) as ApplicationOption[];
  }, [jobApplications, isApplicationsLoading]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, backgroundColor: 'white !important' } }}
    >
      <Box sx={{ px: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardHeader
          title={taskId ? t('tasks.editor-sidesheet.edit.title') : t('tasks.editor-sidesheet.create.title')}
          subheader={t('tasks.editor-sidesheet.form.subheader')}
          subheaderTypographyProps={{ variant: 'caption', mb: 2 }}
        />

        <Divider />

        <CardContent sx={{ flexGrow: 1, overflowY: 'auto' }}>

          {
            canAccess(FeatureType.TASK_MANAGEMENT_ENABLED) ? (
              <FormProvider methods={methods} onSubmit={onSubmit}>
                <Grid container gap={2}>
                  {errorMessage && (
                    <Grid item xs={12}>
                      <Alert variant='outlined' severity='error'>
                        {errorMessage}
                      </Alert>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <RHFTextField
                      name="title"
                      label={t('tasks.editor-sidesheet.form.title.label')}
                      placeholder={t('tasks.editor-sidesheet.form.title.placeholder')}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <RHFTextField
                      name="description"
                      label={t('tasks.editor-sidesheet.form.description.label')}
                      placeholder={t('tasks.editor-sidesheet.form.description.placeholder')}
                      multiline
                      minRows={3}
                      maxRows={5}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <RHFSelect
                      name="priority"
                      label={t('tasks.editor-sidesheet.form.priority.label')}
                      placeholder={t('tasks.editor-sidesheet.form.priority.placeholder')}
                    >
                      <MenuItem value={TaskPriority.NONE}>
                        {t('tasks.editor-sidesheet.form.priority.options.none')}
                      </MenuItem>
                      <MenuItem value={TaskPriority.LOW}>
                        {t('tasks.editor-sidesheet.form.priority.options.low')}
                      </MenuItem>
                      <MenuItem value={TaskPriority.MEDIUM}>
                        {t('tasks.editor-sidesheet.form.priority.options.medium')}
                      </MenuItem>
                      <MenuItem value={TaskPriority.HIGH}>
                        {t('tasks.editor-sidesheet.form.priority.options.high')}
                      </MenuItem>
                    </RHFSelect>
                  </Grid>

                  <Grid item xs={12}>
                    <RHFSelect
                      name="type"
                      label={t('tasks.editor-sidesheet.form.type.label')}
                      placeholder={t('tasks.editor-sidesheet.form.type.placeholder')}
                    >
                      <MenuItem value={TaskType.GENERAL}>
                        {t('tasks.editor-sidesheet.form.type.options.general')}
                      </MenuItem>
                      <MenuItem value={TaskType.TO_DO}>
                        {t('tasks.editor-sidesheet.form.type.options.to_do')}
                      </MenuItem>
                      <MenuItem value={TaskType.CALL}>
                        {t('tasks.editor-sidesheet.form.type.options.call')}
                      </MenuItem>
                      <MenuItem value={TaskType.EMAIL}>
                        {t('tasks.editor-sidesheet.form.type.options.email')}
                      </MenuItem>
                    </RHFSelect>
                  </Grid>

                  <Grid item xs={12}>
                    <RHFTextField
                      name="due_at"
                      label={t('tasks.editor-sidesheet.form.due_at.label')}
                      placeholder={t('tasks.editor-sidesheet.form.due_at.placeholder')}
                      type="datetime-local"

                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Accordion
                      expanded={entityLinksExpanded}
                      onChange={() => setEntityLinksExpanded(!entityLinksExpanded)}
                      sx={{
                        boxShadow: 'none',
                        border: '1px solid',
                        borderColor: 'divider',
                        '&:before': { display: 'none' },
                        borderRadius: 1
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          minHeight: 48,
                          '& .MuiAccordionSummary-content': { margin: '8px 0' }
                        }}
                      >
                        <Stack direction="column" spacing={1} sx={{ width: '100%' }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <LinkIcon fontSize="small" color="primary" />
                            <Typography variant="subtitle2">
                              {t('tasks.editor-sidesheet.form.entity_links.label', 'Linked Entities')}
                            </Typography>
                          </Stack>
                          
                          {/* Show linked entities as chips */}
                          {(watch('company') || watch('contact') || watch('deal') || watch('job') || watch('application')) && (
                            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                              {watch('company') && watch('company')?.company_name && (
                                <Chip
                                  size="small"
                                  label={`${t('tasks.editor-sidesheet.form.company.label')}: ${watch('company')?.company_name}`}
                                  color="primary"
                                  variant="outlined"
                                />
                              )}
                              {watch('contact') && watch('contact')?.name && (
                                <Chip
                                  size="small"
                                  label={`${t('tasks.editor-sidesheet.form.contact.label')}: ${watch('contact')?.name}`}
                                  color="primary"
                                  variant="outlined"
                                />
                              )}
                              {watch('deal') && watch('deal')?.title && (
                                <Chip
                                  size="small"
                                  label={`${t('tasks.editor-sidesheet.form.deal.label')}: ${watch('deal')?.title}`}
                                  color="primary"
                                  variant="outlined"
                                />
                              )}
                              {watch('job') && watch('job')?.title && (
                                <Chip
                                  size="small"
                                  label={`${t('tasks.editor-sidesheet.form.job.label')}: ${watch('job')?.title}`}
                                  color="primary"
                                  variant="outlined"
                                />
                              )}
                              {watch('application') && watch('application')?.title && (
                                <Chip
                                  size="small"
                                  label={`${t('tasks.editor-sidesheet.form.application.label')}: ${watch('application')?.title}`}
                                  color="primary"
                                  variant="outlined"
                                />
                              )}
                            </Stack>
                          )}
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                        <Grid container spacing={2}>
                          {/* Company */}
                          <Grid item xs={12}>
                            <RHFAutocomplete
                              name="company"
                              disabled={!!injectedCompanyId}
                              label={t('tasks.editor-sidesheet.form.company.label')}
                              placeholder={t('tasks.editor-sidesheet.form.company.placeholder')}
                              multiple={false}
                              onInputChange={(event, value) => {
                                onCompanySearchQueryChange(value);
                              }}
                              options={linkableCompanies as CompanyOption[] ?? []}
                              renderOption={(props, option: CompanyOption, { selected }) => (
                                <li {...props}>
                                  <Typography variant="body2">{option.company_name}</Typography>
                                </li>
                              )}
                              getOptionLabel={(option: CompanyOption | string) =>
                                typeof option === 'string' ? option : option.company_name
                              }
                              isOptionEqualToValue={(option: CompanyOption, value: CompanyOption) => option.id === value.id}
                              getOptionKey={(option) => (option as CompanyOption).id as string}
                              size="small"
                            />
                          </Grid>

                          {/* Contact */}
                          <Grid item xs={12}>
                            <RHFAutocomplete
                              name="contact"
                              disabled={!!injectedContactId}
                              label={t('tasks.editor-sidesheet.form.contact.label')}
                              placeholder={t('tasks.editor-sidesheet.form.contact.placeholder')}
                              multiple={false}
                              onInputChange={(event, value) => {
                                onContactSearchQueryChange(value);
                              }}
                              options={linkableContacts as ContactOption[] ?? []}
                              renderOption={(props, option: ContactOption, { selected }) => (
                                <li {...props}>
                                  <Typography variant="body2">
                                    {`${option.name} (${option.email})`}
                                  </Typography>
                                </li>
                              )}
                              getOptionLabel={(option: ContactOption | string) =>
                                typeof option === 'string' ? option : `${option.name}`
                              }
                              isOptionEqualToValue={(option: ContactOption, value: ContactOption) => option.id === value.id}
                              getOptionKey={(option) => (option as ContactOption).id as string}
                              size="small"
                            />
                          </Grid>

                          {/* Deal */}
                          <Grid item xs={12}>
                            <RHFAutocomplete
                              name="deal"
                              disabled={!!injectedDealId}
                              label={t('tasks.editor-sidesheet.form.deal.label')}
                              placeholder={t('tasks.editor-sidesheet.form.deal.placeholder')}
                              multiple={false}
                              onInputChange={(event, value) => {
                                onDealsSearchQueryChange(value);
                              }}
                              options={linkableDeals as DealOption[] ?? []}
                              renderOption={(props, option: DealOption, { selected }) => (
                                <li {...props}>
                                  <Typography variant="body2">
                                    {`${option.title}`}
                                  </Typography>
                                </li>
                              )}
                              getOptionLabel={(option: DealOption | string) =>
                                typeof option === 'string' ? option : `${option.title}`
                              }
                              isOptionEqualToValue={(option: DealOption, value: DealOption) => option.id === value.id}
                              getOptionKey={(option) => (option as DealOption).id as string}
                              size="small"
                            />
                          </Grid>

                          {/* Job */}
                          <Grid item xs={12}>
                            <RHFAutocomplete
                              name="job"
                              disabled={!!injectedJobId}
                              label={t('tasks.editor-sidesheet.form.job.label')}
                              placeholder={t('tasks.editor-sidesheet.form.job.placeholder')}
                              multiple={false}
                              onInputChange={(event, value) => {
                                onJobsSearchQueryChange(value);
                              }}
                              options={linkableJobs as JobOption[] ?? []}
                              renderOption={(props, option: JobOption, { selected }) => (
                                <li {...props}>
                                  <Typography variant="body2">
                                    {`${option.title}`}
                                  </Typography>
                                </li>
                              )}
                              getOptionLabel={(option: JobOption | string) =>
                                typeof option === 'string' ? option : `${option.title}`
                              }
                              isOptionEqualToValue={(option: JobOption, value: JobOption) => option.id === value.id}
                              getOptionKey={(option) => (option as JobOption).id as string}
                              size="small"
                            />
                          </Grid>

                          {/* Application */}
                          <Grid item xs={12}>
                            <RHFAutocomplete
                              name="application"
                              disabled={!!injectedApplicationId}
                              label={t('tasks.editor-sidesheet.form.application.label')}
                              placeholder={t('tasks.editor-sidesheet.form.application.placeholder')}
                              multiple={false}
                              onInputChange={(event, value) => {
                                onApplicationsSearchQueryChange(value);
                              }}
                              options={linkableApplications as ApplicationOption[] ?? []}
                              renderOption={(props, option: ApplicationOption, { selected }) => (
                                <li {...props}>
                                  <Typography variant="body2">
                                    {`${option.title}`}
                                  </Typography>
                                </li>
                              )}
                              getOptionLabel={(option: ApplicationOption | string) =>
                                typeof option === 'string' ? option : `${option.title}`
                              }
                              isOptionEqualToValue={(option: ApplicationOption, value: ApplicationOption) => option.id === value.id}
                              getOptionKey={(option) => (option as ApplicationOption).id as string}
                              size="small"
                            />
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>

                  {
                    [AccountRole.Admin, AccountRole.Op_Manager].includes(authContext.account.org_role) && (
                      <Grid item xs={12}>
                        <RHFAutocomplete
                          sx={{
                            paddingTop: 2,
                          }}
                          name="assigned_to"
                          label={t('tasks.editor-sidesheet.form.assigned_to.label')}
                          placeholder={t('tasks.editor-sidesheet.form.assigned_to.placeholder')}
                          multiple={false}
                          onInputChange={(event, value) => {
                            onAccountSearchQueryChange(value);
                          }}
                          options={linkableAccounts as AccountOption[] ?? []}
                          renderOption={(props, option: AccountOption, { selected }) => (
                            <li {...props}>
                              <Stack direction="row">
                                <Typography variant="body1">
                                  {`${option.name} (${option.email})`}
                                </Typography>
                              </Stack>
                            </li>
                          )}
                          getOptionLabel={(option: AccountOption | string) =>
                            typeof option === 'string' ? option : `${option.name}`
                          }
                          isOptionEqualToValue={(option: AccountOption, value: AccountOption) => option.id === value.id}
                          getOptionKey={(option) => (option as AccountOption).id as string}
                        />
                      </Grid>
                    )
                  }
                </Grid>
              </FormProvider>
            ) : (
              <FeatureLockedListView text={t('billing.subscription_required')} onNavigate={onClose} />
            )
          }

        </CardContent>
        <Divider />
        <CardActions>
          <Stack direction="row" spacing={2} sx={{ width: '100%', my: 2 }} justifyContent="flex-end">
            <LoadingButton
              variant="outlined"
              onClick={handleClose}
            >
              {t('common.cancel')}
            </LoadingButton>
            <LoadingButton
              type="submit"
              variant="contained"
              onClick={onSubmit}
              disabled={!canAccess(FeatureType.TASK_MANAGEMENT_ENABLED) || updateTaskLoading || createTaskLoading}
              loading={createTaskLoading || updateTaskLoading}
            >
              {taskId ? t('tasks.editor-sidesheet.form.update') : t('tasks.editor-sidesheet.form.create')}
            </LoadingButton>
          </Stack>
        </CardActions>
      </Box>
    </Drawer>
  );
}